import {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {ControlButton} from "@ui/buttons/ControlButton";
import {RootIcon} from "@ui/icons/RootIcon";
import {
  GridLayout,
  useContainerWidth,
  verticalCompactor,
} from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import {useTranslation} from "react-i18next";
import "react-resizable/css/styles.css";

import styles from "../styles.module.scss";
import {CriteriaSectionBlock} from "./CriteriaSectionBlock";
import {
  GRID_MARGIN_Y,
  GRID_ROW_HEIGHT,
  SECTION_GRID_COLS,
  addCriteriaSection,
  applyRowPositions,
  applySectionLayoutChange,
  filterSectionCardLayout,
  isSameLayout,
  pixelsToGridH,
  removeCriteriaSection,
  removeSubcriterion,
  reorderSectionsByLayout,
  sortLayoutItems,
  updateCriteriaSection,
  updateSubcriterionField,
} from "./criteriaGridHelpers";

const SectionGridItem = ({
  section,
  readOnly,
  canRemove,
  measureRef,
  onRemove,
  onChange,
  target,
  t,
}) => (
  <div
    ref={measureRef}
    className={styles.sectionGridItem}
  >
    {!readOnly && (
      <div className={styles.sectionGridItemHeader}>
        <span className={`${styles.gridItemDragHandle} sectionDragHandle`}>
          {Array.from({length: 6}, (_, index) => (
            <span
              key={index}
              className={styles.gridItemDragDot}
            />
          ))}
        </span>

        {canRemove && (
          <button
            type={"button"}
            className={styles.gridItemRemove}
            onClick={() => onRemove(section.id)}
            aria-label={t("table.button.delete")}
          >
            <RootIcon
              id={"delete"}
              fill={"red"}
              width={16}
              height={16}
            />
          </button>
        )}
      </div>
    )}

    <CriteriaSectionBlock
      title={section.title}
      layout={section.layout}
      subcriteria={section.subcriteria}
      readOnly={readOnly}
      onTitleChange={(value) =>
        onChange(updateCriteriaSection(target, section.id, {title: value}))
      }
      onGridReorder={({subcriteria, layout}) =>
        onChange(
          updateCriteriaSection(target, section.id, {subcriteria, layout}),
        )
      }
      onSubcriterionChange={(subId, field, value) =>
        onChange(
          updateSubcriterionField(target, section.id, subId, field, value),
        )
      }
      onAddSubcriterion={(subcriteria, layout) =>
        onChange(
          updateCriteriaSection(target, section.id, {subcriteria, layout}),
        )
      }
      onRemoveSubcriterion={(subId) =>
        onChange(removeSubcriterion(target, section.id, subId))
      }
    />
  </div>
);

export const CriteriaSectionsList = ({target, readOnly = false, onChange}) => {
  const {t} = useTranslation();
  const {width, containerRef, mounted} = useContainerWidth();
  const [measuredGridH, setMeasuredGridH] = useState({});
  const measureObserversRef = useRef({});
  const measureCallbacksRef = useRef(new Map());

  const sections = target?.sections ?? [];
  const sectionsLayout = target?.sectionsLayout ?? [];

  const orderedSections = useMemo(
    () => reorderSectionsByLayout(sections, sectionsLayout),
    [sections, sectionsLayout],
  );

  const sizedLayout = useMemo(() => {
    const cards = sortLayoutItems(filterSectionCardLayout(sectionsLayout)).map(
      (item) => ({
        ...item,
        h: measuredGridH[item.i] ?? item.h,
      }),
    );

    return applyRowPositions(cards, SECTION_GRID_COLS);
  }, [sectionsLayout, measuredGridH]);

  const displayLayout = sizedLayout;

  useEffect(() => {
    const activeIds = new Set(sections.map((section) => section.id));

    Object.entries(measureObserversRef.current).forEach(([id, observer]) => {
      if (!activeIds.has(id)) {
        observer.disconnect();
        delete measureObserversRef.current[id];
        measureCallbacksRef.current.delete(id);
      }
    });

    setMeasuredGridH((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([id]) => activeIds.has(id)),
      );

      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, [sections]);

  useEffect(() => {
    return () => {
      Object.values(measureObserversRef.current).forEach((observer) => {
        observer.disconnect();
      });
      measureObserversRef.current = {};
      measureCallbacksRef.current.clear();
    };
  }, []);

  const getMeasureRef = useCallback((id) => {
    if (!measureCallbacksRef.current.has(id)) {
      measureCallbacksRef.current.set(id, (node) => {
        measureObserversRef.current[id]?.disconnect();
        delete measureObserversRef.current[id];

        if (!node) {
          return;
        }

        const reportHeight = () => {
          const gridH = pixelsToGridH(node.scrollHeight);

          setMeasuredGridH((prev) =>
            prev[id] === gridH ? prev : {...prev, [id]: gridH},
          );
        };

        const observer = new ResizeObserver(() => {
          reportHeight();
        });

        observer.observe(node);
        measureObserversRef.current[id] = observer;
        requestAnimationFrame(reportHeight);
      });
    }

    return measureCallbacksRef.current.get(id);
  }, []);

  const syncSectionLayout = useCallback(
    (nextLayout) => {
      const merged = filterSectionCardLayout(nextLayout).map((item) => ({
        ...item,
        h: measuredGridH[item.i] ?? item.h,
      }));
      const next = applySectionLayoutChange(target, merged);
      const sameOrder = sections.every(
        (item, index) => item.id === next.sections[index]?.id,
      );

      if (isSameLayout(sectionsLayout, next.sectionsLayout) && sameOrder) {
        return;
      }

      onChange?.(next);
    },
    [target, sections, sectionsLayout, measuredGridH, onChange],
  );

  const handleAdd = () => {
    onChange?.(addCriteriaSection(target));
  };

  return (
    <div className={styles.sectionsGrid}>
      <div
        ref={containerRef}
        className={styles.sectionsGridContainer}
      >
        {mounted && width > 0 && (
          <GridLayout
            width={width}
            layout={displayLayout}
            onLayoutChange={readOnly ? undefined : syncSectionLayout}
            onDragStop={readOnly ? undefined : syncSectionLayout}
            compactor={verticalCompactor}
            gridConfig={{
              cols: SECTION_GRID_COLS,
              rowHeight: GRID_ROW_HEIGHT,
              margin: [12, GRID_MARGIN_Y],
            }}
            dragConfig={{
              enabled: !readOnly,
              handle: ".sectionDragHandle",
            }}
            resizeConfig={{enabled: false}}
          >
            {orderedSections.map((section) => (
              <div key={section.id}>
                <SectionGridItem
                  section={section}
                  readOnly={readOnly}
                  canRemove={!readOnly && sections.length > 1}
                  measureRef={getMeasureRef(section.id)}
                  onRemove={(sectionId) =>
                    onChange?.(removeCriteriaSection(target, sectionId))
                  }
                  onChange={onChange}
                  target={target}
                  t={t}
                />
              </div>
            ))}
          </GridLayout>
        )}
      </div>

      {!readOnly && (
        <div style={{marginRight: "auto"}}>
          <ControlButton
            color={"var(--violetLight)"}
            txt={t("button.add_criterion")}
            onClick={handleAdd}
          />
        </div>
      )}
    </div>
  );
};
